import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SavedWord, ActivityType } from '../../../types/vocabulary';
import { fetchImageWithFallback } from '../../../services/imageService';
import { searchWord } from '../../../services/dictionary';
import { translateText, translateFallback } from '../../../hooks/useTranslation';
import { getRandomWords } from '../../activities/words';
import { Volume2, Mic, Image as ImageIcon, Check, X, ArrowRight, Play, SkipForward, Gauge, Loader2 } from 'lucide-react';
import { useSpeechRecognition } from '../../../hooks/useSpeechRecognition';
import { PracticeModal } from '../../PracticeModal';
import { playCorrectSound, playWrongSound } from '../../../utils/soundEffects';
import { useSpeechSynthesis, selectBestVoice } from '../../../hooks/useSpeechSynthesis';
import { compareTextsV2 } from '../../../utils/textComparison';
import { analyzeSentence, GrammarError } from '../../../services/grammarValidationService';
import { themes } from '../../../utils/themes';

// To prevent garbage collection in Chrome which causes speech to stop halfway
let activeUtterances: SpeechSynthesisUtterance[] = [];

const getDecoyWordsForTheme = (count: number, targetWord: string, themeId?: string) => {
    let pool: string[] = [];
    if (themeId) {
        const theme = themes.find(t => t.id === themeId || t.name === themeId);
        if (theme) {
            const allThemeItems = [...theme.words, ...(theme.sentences || [])];
            pool = allThemeItems.filter(w => w.toLowerCase() !== targetWord.toLowerCase());
        }
    }
    // If not enough words in theme, fallback to global words
    if (pool.length < count) {
        const globalDecoys = getRandomWords(count);
        const additional = globalDecoys.filter(gw => gw.toLowerCase() !== targetWord.toLowerCase() && !pool.includes(gw));
        pool = [...pool, ...additional];
    }
    return pool.sort(() => Math.random() - 0.5).slice(0, count);
};

// Helper function to speak text with good voice selection
const speakText = (text: string, lang: string = 'en-US') => {
    if (!text || typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    
    // Cancel previous speech to avoid overlapping or queuing if clicked rapidly
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    activeUtterances.push(utterance);
    
    utterance.lang = lang;
    utterance.rate = 0.85;
    
    utterance.onend = () => {
        activeUtterances = activeUtterances.filter(u => u !== utterance);
    };
    utterance.onerror = () => {
        activeUtterances = activeUtterances.filter(u => u !== utterance);
    };
    
    const setVoiceAndSpeak = () => {
        const voices = window.speechSynthesis.getVoices();
        
        const bestVoice = selectBestVoice(voices, lang);
            
        if (bestVoice) {
            utterance.voice = bestVoice;
        }
        
        window.speechSynthesis.speak(utterance);
    };
    
    // Check if voices are already loaded
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) {
        setVoiceAndSpeak();
    } else {
        // Wait for voices to load
        window.speechSynthesis.addEventListener('voiceschanged', setVoiceAndSpeak, { once: true });
    }
};

// --- Shared Props ---
interface ActivityProps {
  word: SavedWord;
  onComplete: (success: boolean, userAnswer?: string, correctAnswer?: string, score?: number) => void;
  onUpdateWord?: (updates: Partial<SavedWord>) => Promise<void>;
}

// --- Helper Components ---

const Feedback = ({ success, onNext, correctAnswer, userAnswer, comparisonResults, grammarFeedback, grammarErrors, standalone = false }: { 
  success: boolean, 
  onNext: () => void, 
  correctAnswer?: string | null, 
  userAnswer?: string, 
  comparisonResults?: Array<{ original: string; spoken: string; status: 'correct' | 'close' | 'incorrect' }> | null,
  grammarFeedback?: string,
  grammarErrors?: GrammarError[],
  standalone?: boolean 
}) => {
  useEffect(() => {
    if (success) {
      playCorrectSound();
    } else {
      playWrongSound();
    }
  }, [success]);

  const feedbackContent = (
    <>
      <div className={`p-4 rounded-lg flex items-center justify-between ${standalone ? 'mb-4' : ''} ${success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        <div className="flex items-center gap-2">
            {success ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
            <span className="font-medium">{success ? 'Correct!' : 'Incorrect'}</span>
        </div>
        <button onClick={onNext} className="bg-white/50 hover:bg-white/80 px-4 py-1 rounded-full text-sm font-semibold transition-colors flex items-center gap-1">
            Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      {/* Show gray background section if there's content to display */}
      {(userAnswer !== undefined || (!success && (correctAnswer || comparisonResults && comparisonResults.length > 0))) && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center space-y-2">
          {userAnswer !== undefined && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Your answer:</p>
              <p className={`font-medium text-lg ${success ? 'text-green-700' : 'text-red-700'}`}>
                {userAnswer.trim() || '(empty)'}
              </p>
            </div>
          )}
          {!success && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Correct answer:</p>
              {correctAnswer ? (
                <p className="font-medium text-green-700 text-lg">{correctAnswer}</p>
              ) : (
                <p className="font-medium text-gray-600 text-lg italic">Translation not available</p>
              )}
            </div>
          )}
          {!success && userAnswer === undefined && !correctAnswer && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Your answer:</p>
              <p className="font-medium text-lg text-red-700">(empty)</p>
            </div>
          )}
          {/* Show detailed errors if comparisonResults are available */}
          {!success && comparisonResults && comparisonResults.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 text-left">
              <h4 className="text-sm font-medium text-gray-600 mb-2">Feedback:</h4>
              <ul className="space-y-2">
                {comparisonResults
                  .filter(result => result.status !== 'correct')
                  .map((result, i) => {
                    const originalClean = result.original || '';
                    const spokenClean = result.spoken || '(missed)';
                    return (
                      <li key={i} className="text-sm">
                        <span className="font-medium">{originalClean}</span>:{' '}
                        {result.status === 'close'
                          ? `Close, but not perfect. You said "${spokenClean}". Try again!`
                          : `Incorrect. You said "${spokenClean}"`}
                      </li>
                    );
                  })}
                {comparisonResults.every((result) => result.status === 'correct') && (
                  <li className="text-sm">
                    <span className="font-medium">Your pronunciation is perfect!</span>
                  </li>
                )}
              </ul>
            </div>
          )}
          {/* Show grammar validation errors */}
          {grammarFeedback && (
            <div className="mt-4 pt-4 border-t border-gray-200 text-left">
              <h4 className={`text-sm font-medium mb-3 ${success ? 'text-green-700' : 'text-red-700'}`}>{grammarFeedback}</h4>
              {grammarErrors && grammarErrors.length > 0 && (
                <ul className="space-y-3">
                  {grammarErrors.map((error, i) => (
                    <li key={i} className="text-sm bg-white p-3 rounded-md border border-red-100 shadow-sm">
                      <p className="font-medium text-red-600 mb-1">{error.message}</p>
                      <p className="text-gray-600 italic mb-2">"... {error.context} ..."</p>
                      {error.suggestions.length > 0 && (
                        <p className="text-green-600">
                          <span className="font-medium">Suggestion:</span> {error.suggestions.join(', ')}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );

  if (standalone) {
    return (
      <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        {feedbackContent}
      </div>
    );
  }

  return <>{feedbackContent}</>;
};

const TextToSpeech = ({ text }: { text: string }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [activeAccent, setActiveAccent] = useState<'en-US' | 'en-GB'>('en-US');
    const voicesChangedHandlerRef = useRef<(() => void) | null>(null);
    const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            // Clean up event listener
            if (voicesChangedHandlerRef.current && 'speechSynthesis' in window) {
                window.speechSynthesis.removeEventListener('voiceschanged', voicesChangedHandlerRef.current);
                voicesChangedHandlerRef.current = null;
            }
            // Cancel any ongoing speech
            if ('speechSynthesis' in window) {
                window.speechSynthesis.cancel();
            }
            setIsPlaying(false);
        };
    }, []);

    const handleSpeak = (lang: 'en-US' | 'en-GB' = 'en-US') => {
        if (!text) return;
        setActiveAccent(lang);
        
        if ('speechSynthesis' in window) {
            const synth = window.speechSynthesis;
            
            // Cancel any existing speech
            synth.cancel();
            setIsPlaying(false);
            
            const utterance = new SpeechSynthesisUtterance(text);
            utteranceRef.current = utterance;
            utterance.lang = lang;
            utterance.rate = 0.85;
            
            const setVoiceAndSpeak = () => {
                // Remove the event listener since we're handling it
                if (voicesChangedHandlerRef.current) {
                    synth.removeEventListener('voiceschanged', voicesChangedHandlerRef.current);
                    voicesChangedHandlerRef.current = null;
                }
                
                const voices = synth.getVoices();
                
                const bestVoice = selectBestVoice(voices, lang);
                
                if (bestVoice) {
                    utterance.voice = bestVoice;
                    console.log('[TextToSpeech] Using voice:', bestVoice.name);
                } else {
                    console.log('[TextToSpeech] No preferred voice found, using default');
                }
                
                utterance.onend = () => {
                    setIsPlaying(false);
                    utteranceRef.current = null;
                };
                utterance.onerror = (e) => {
                    console.warn('[TextToSpeech] Speech error:', e.error);
                    setIsPlaying(false);
                    utteranceRef.current = null;
                };
                
                try {
                    synth.speak(utterance);
                    setIsPlaying(true);
                } catch (error) {
                    console.error('[TextToSpeech] Error speaking:', error);
                    setIsPlaying(false);
                    utteranceRef.current = null;
                }
            };
            
            // Check if voices are already loaded
            const voices = synth.getVoices();
            if (voices.length > 0) {
                setVoiceAndSpeak();
            } else {
                // Wait for voices to load - store handler for cleanup
                voicesChangedHandlerRef.current = setVoiceAndSpeak;
                synth.addEventListener('voiceschanged', setVoiceAndSpeak, { once: true });
            }
        }
    };

    const handleStop = () => {
        if ('speechSynthesis' in window) {
            const synth = window.speechSynthesis;
            synth.cancel();
            setIsPlaying(false);
            utteranceRef.current = null;
            // Clean up event listener if still pending
            if (voicesChangedHandlerRef.current) {
                synth.removeEventListener('voiceschanged', voicesChangedHandlerRef.current);
                voicesChangedHandlerRef.current = null;
            }
        }
    };

    return (
        <div className="flex items-center justify-center gap-3">
            <button 
                onClick={isPlaying && activeAccent === 'en-US' ? handleStop : () => handleSpeak('en-US')} 
                className={`px-4 py-2 rounded-full font-semibold text-sm flex items-center gap-1.5 transition-colors ${
                    isPlaying && activeAccent === 'en-US'
                        ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
                title={isPlaying && activeAccent === 'en-US' ? 'Stop playback' : 'Play US audio'}
                data-testid="tts-play-us"
            >
                <Volume2 className="w-5 h-5" />
                <span>🇺🇸 US</span>
            </button>
            <button 
                onClick={isPlaying && activeAccent === 'en-GB' ? handleStop : () => handleSpeak('en-GB')} 
                className={`px-4 py-2 rounded-full font-semibold text-sm flex items-center gap-1.5 transition-colors ${
                    isPlaying && activeAccent === 'en-GB'
                        ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                        : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                }`}
                title={isPlaying && activeAccent === 'en-GB' ? 'Stop playback' : 'Play UK audio'}
                data-testid="tts-play-uk"
            >
                <Volume2 className="w-5 h-5" />
                <span>🇬🇧 UK</span>
            </button>
        </div>
    );
};

// --- Activities ---

export const ReviewCard: React.FC<ActivityProps> = ({ word, onComplete }) => {
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [wordData, setWordData] = useState<{
    translation?: string;
    definition?: string;
    example?: string;
    phonetic?: string;
  } | null>(null);

  // Automatically speak the word when component mounts
  useEffect(() => {
    if (word?.word) {
      // Small delay to ensure voices are loaded
      const timer = setTimeout(() => {
        speakText(word.word, 'en-US');
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [word?.word]);

  useEffect(() => {
    const fetchWordData = async () => {
      if (!revealed) return;
      
      // If word already has translation and example, use those
      if (word.translation && word.exampleSentence) {
        setWordData({
          translation: word.translation,
          definition: undefined,
          example: word.exampleSentence,
          phonetic: undefined
        });
        return;
      }

      setLoading(true);
      
      let dictResult: any = null;
      try {
        dictResult = await searchWord(word.word);
      } catch (error) {
        console.error('Error fetching word dictionary details:', error);
      }
      
      let translation = word.translation;
      if (!translation) {
        try {
          translation = await translateText(word.word, 'en', 'pt');
          if (!translation || translation.toLowerCase() === word.word.toLowerCase()) {
            translation = await translateFallback(word.word);
          }
        } catch (error) {
          console.warn('Translation failed, trying fallback:', error);
          try {
            translation = await translateFallback(word.word);
          } catch (fallbackError) {
            console.warn('Fallback translation failed:', fallbackError);
            if (dictResult?.meanings && dictResult.meanings.length > 0) {
              translation = dictResult.meanings[0].definitions[0]?.definition || '';
            }
          }
        }
      }

      let example = word.exampleSentence;
      if (!example && dictResult?.examples?.length > 0) {
        example = dictResult.examples[0];
      } else if (!example && dictResult?.meanings?.length > 0) {
        example = dictResult.meanings[0].definitions[0]?.example || '';
      }

      const cleanExample = example ? example.replace(/{\/?it}|\/it\//g, '').replace(/{\/?phrase}/g, '').replace(/\s+/g, ' ').trim() : undefined;

      setWordData({
        translation: translation || undefined,
        definition: dictResult?.definition || dictResult?.meanings?.[0]?.definitions?.[0]?.definition || undefined,
        example: cleanExample || undefined,
        phonetic: dictResult?.phonetic || undefined
      });

      setLoading(false);
    };

    fetchWordData();
  }, [revealed, word.word, word.translation, word.exampleSentence]);

  return (
    <div className="flex flex-col items-center justify-center p-6 bg-white rounded-xl shadow-sm border border-gray-100 max-w-md mx-auto min-h-[400px]">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-3">{word.word}</h2>
        <div className="flex items-center justify-center gap-2 mb-4">
          <button
            type="button"
            onClick={() => speakText(word.word, 'en-US')}
            className="px-3 py-1 text-xs font-semibold bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 rounded-full flex items-center gap-1 transition-colors"
            title="Listen in US English"
          >
            <Volume2 className="w-3.5 h-3.5" />
            <span>🇺🇸 US</span>
          </button>
          <button
            type="button"
            onClick={() => speakText(word.word, 'en-GB')}
            className="px-3 py-1 text-xs font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 rounded-full flex items-center gap-1 transition-colors"
            title="Listen in British English"
          >
            <Volume2 className="w-3.5 h-3.5" />
            <span>🇬🇧 UK</span>
          </button>
        </div>
        {word.imageUrl && <img referrerPolicy="no-referrer" src={word.imageUrl} alt={word.word} className="w-32 h-32 object-cover rounded-lg mx-auto mb-4" />}
      </div>

      {!revealed ? (
        <button
            onClick={() => setRevealed(true)}
            className="px-6 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors font-medium"
        >
            Reveal
        </button>
      ) : (
        <div className="text-center space-y-4 w-full">
            {loading ? (
                <div className="py-8">
                    <p className="text-gray-500">Loading word details...</p>
                </div>
            ) : (
                <>
                    {wordData?.phonetic && (
                        <p className="text-sm text-gray-500 italic">/{wordData.phonetic}/</p>
                    )}
                    
                    {wordData?.definition && (
                        <div className="mb-4">
                            <p className="text-sm text-gray-500 mb-1">Definition:</p>
                            <p className="text-lg text-gray-700">{wordData.definition}</p>
                        </div>
                    )}

                    {wordData?.translation && (
                        <div className="mb-4 pb-3 border-b">
                            <p className="text-sm text-gray-500 mb-1">Translation:</p>
                            <p className="text-xl text-gray-600 font-medium">{wordData.translation}</p>
                        </div>
                    )}

                    {wordData?.example && (
                        <div className="mb-4">
                            <p className="text-sm text-gray-500 mb-1">Example:</p>
                            <p className="text-gray-500 italic">"{wordData.example}"</p>
                        </div>
                    )}

                    {!wordData?.translation && !wordData?.example && !wordData?.definition && (
                        <p className="text-sm text-gray-400 italic">Unable to load word details</p>
                    )}
                </>
            )}

            <div className="pt-6">
                <button
                    onClick={() => onComplete(true)}
                    className="px-8 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors font-bold shadow-md w-full"
                >
                    Continue
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export const SentenceFormation: React.FC<ActivityProps> = ({ word, onComplete }) => {
    const [input, setInput] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [success, setSuccess] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [grammarFeedback, setGrammarFeedback] = useState<string>('');
    const [grammarErrors, setGrammarErrors] = useState<GrammarError[]>([]);

    // Automatically speak the word when component mounts
    useEffect(() => {
        if (word?.word) {
            const timer = setTimeout(() => {
                speakText(word.word, 'en-US');
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [word?.word]);

    const check = async () => {
        if (!input.trim() || isChecking) return;
        setIsChecking(true);
        try {
            const result = await analyzeSentence(word.word, input);
            setSuccess(result.isCorrect);
            setGrammarFeedback(result.feedback);
            setGrammarErrors(result.errors || []);
            setSubmitted(true);
        } catch (error) {
            console.error('Error in validation:', error);
            const isCorrect = input.toLowerCase().includes(word.word.toLowerCase()) && input.length > word.word.length + 5;
            setSuccess(isCorrect);
            setSubmitted(true);
        } finally {
            setIsChecking(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Submit on Enter
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            check();
        }
    };

    if (submitted) return <Feedback success={success} onNext={() => onComplete(success, input)} userAnswer={input} grammarFeedback={grammarFeedback} grammarErrors={grammarErrors} />;

    return (
        <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Write a sentence using: <span className="font-bold text-purple-600">{word.word}</span></h3>
            <textarea
                className="w-full border-2 border-gray-200 rounded-lg p-3 focus:border-purple-500 focus:outline-none transition-colors mb-4"
                rows={3}
                placeholder="Type here... (Press Enter to submit)"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isChecking}
            />
            <button 
                onClick={check} 
                disabled={isChecking || !input.trim()}
                className="w-full py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
                {isChecking && <Loader2 className="w-5 h-5 animate-spin" />}
                {isChecking ? 'Checking...' : 'Check'}
            </button>
        </div>
    );
};

// Helper function to highlight differences between two strings
const highlightDifferences = (userText: string, correctText: string) => {
    if (!userText || !correctText) {
        return <span className="text-gray-600 italic">No comparison available</span>;
    }

    const userWords = userText.trim().split(/\s+/);
    const correctWords = correctText.trim().split(/\s+/);
    const maxLength = Math.max(userWords.length, correctWords.length);
    const result: JSX.Element[] = [];

    for (let i = 0; i < maxLength; i++) {
        const userWord = (userWords[i] || '').toLowerCase();
        const correctWord = (correctWords[i] || '').toLowerCase();
        const originalUserWord = userWords[i] || '';
        const originalCorrectWord = correctWords[i] || '';
        
        if (userWord === correctWord && userWord !== '') {
            // Word matches - show in green
            result.push(
                <span key={i} className="text-green-700 font-medium">
                    {originalCorrectWord}{' '}
                </span>
            );
        } else {
            // Word doesn't match or missing - highlight difference
            if (userWord && correctWord) {
                // Both exist but different - show user's word in red, correct in green
                result.push(
                    <span key={i} className="bg-red-200 text-red-800 font-medium px-1 rounded">
                        {originalUserWord}
                    </span>
                );
                result.push(
                    <span key={`correct-${i}`} className="text-green-700 font-medium ml-1">
                        → {originalCorrectWord}{' '}
                    </span>
                );
            } else if (userWord && !correctWord) {
                // Extra word in user's answer
                result.push(
                    <span key={i} className="bg-red-200 text-red-800 font-medium line-through px-1 rounded">
                        {originalUserWord}{' '}
                    </span>
                );
            } else if (!userWord && correctWord) {
                // Missing word
                result.push(
                    <span key={i} className="bg-yellow-200 text-yellow-800 font-medium px-1 rounded">
                        [missing: {originalCorrectWord}]{' '}
                    </span>
                );
            }
        }
    }
    
    return <div className="flex flex-wrap gap-1">{result}</div>;
};

export const ListenToWrite: React.FC<ActivityProps> = ({ word, onComplete }) => {
    const [input, setInput] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [success, setSuccess] = useState(false);

    // Dictate word or sentence, removing any formatting tags
    const rawText = Math.random() > 0.5 && word.exampleSentence ? word.exampleSentence : word.word;
    const textToPlay = rawText.replace(/{\/?it}|\/it\//g, '').replace(/{\/?phrase}/g, '').replace(/\s+/g, ' ').trim();

    // Automatically speak the text when component mounts
    useEffect(() => {
        if (textToPlay) {
            const timer = setTimeout(() => {
                speakText(textToPlay, 'en-US');
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [textToPlay]);

    const check = () => {
        // Remove punctuation for comparison
        const cleanInput = input.toLowerCase().replace(/[.,\/#!$%^&*;:{}=\-_`~()?"'[\]<>…]/g,"").trim();
        const cleanTarget = textToPlay.toLowerCase().replace(/[.,\/#!$%^&*;:{}=\-_`~()?"'[\]<>…]/g,"").trim();

        const isCorrect = cleanInput === cleanTarget;
        setSuccess(isCorrect);
        setSubmitted(true);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            check();
        }
    };

    if (submitted) return (
        <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100">
             <Feedback success={success} onNext={() => onComplete(success, input, textToPlay)} />
             <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
                 <div>
                     <p className="text-sm text-gray-500 mb-1">What you wrote:</p>
                     <p className={`font-medium ${success ? 'text-green-700' : 'text-red-700'}`}>
                         {input || '(empty)'}
                     </p>
                 </div>
                 {!success && (
                     <>
                         <div>
                             <p className="text-sm text-gray-500 mb-1">Correct answer:</p>
                             <p className="font-medium text-green-700">{textToPlay}</p>
                         </div>
                         <div className="pt-2 border-t border-gray-200">
                             <p className="text-sm text-gray-500 mb-2">Differences:</p>
                             <div className="text-sm leading-relaxed">
                                 {highlightDifferences(input, textToPlay)}
                             </div>
                         </div>
                     </>
                 )}
             </div>
        </div>
    );

    return (
        <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
            <h3 className="text-lg font-medium text-gray-700 mb-6">Listen and type what you hear</h3>
            <div className="mb-6 flex justify-center">
                <TextToSpeech text={textToPlay} />
            </div>
            <input
                type="text"
                className="w-full border-2 border-gray-200 rounded-lg p-3 focus:border-purple-500 focus:outline-none transition-colors mb-4 text-center"
                placeholder="Type what you hear... (Press Enter to submit)"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
            />
            <button onClick={check} className="w-full py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors">
                Check
            </button>
        </div>
    );
};

export const Translation: React.FC<ActivityProps> = ({ word, onComplete }) => {
    const [input, setInput] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [success, setSuccess] = useState(false);
    const [translation, setTranslation] = useState<string | null>(word.translation || null);
    const [isFetchingTranslation, setIsFetchingTranslation] = useState(false);

    // Automatically speak the word when component mounts
    useEffect(() => {
        if (word?.word) {
            const timer = setTimeout(() => {
                speakText(word.word, 'en-US');
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [word?.word]);

    // Update translation when word.translation changes
    useEffect(() => {
        if (word.translation) {
            setTranslation(word.translation);
        }
    }, [word.translation]);

    const check = async () => {
        // Prevent multiple submissions
        if (submitted || isFetchingTranslation) {
            return;
        }

        // Don't allow empty submissions
        if (!input.trim()) {
            return;
        }

        let targetTranslation = translation;

        // If translation is missing, fetch it
        if (!targetTranslation) {
            setIsFetchingTranslation(true);
            try {
                const fetchedTranslation = await translateText(word.word, 'en', 'pt');
                if (fetchedTranslation) {
                    targetTranslation = fetchedTranslation;
                    setTranslation(fetchedTranslation);
                }
            } catch (error) {
                console.error('Error fetching translation:', error);
                // If translation fetch fails, mark as incorrect
                setSuccess(false);
                setSubmitted(true);
                setIsFetchingTranslation(false);
                return;
            } finally {
                setIsFetchingTranslation(false);
            }
        }

        // If still no translation after fetch attempt, mark as incorrect
        if (!targetTranslation) {
            setSuccess(false);
            setSubmitted(true);
            return;
        }
        
        const normalizeText = (text: string) => {
            return text
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .replace(/[.,\/#!$%^&*;:{}=\-_`~()?"'[\]<>…]/g,"") // remove punctuation
                .replace(/\b(o|a|os|as|um|uma|uns|umas|the|an)\b/g, '') // remove articles
                .replace(/\s+/g, ' ')
                .trim();
        };
        
        const cleanInput = normalizeText(input);
        const cleanTarget = normalizeText(targetTranslation);
        
        // Split by comma or semicolon and check if any part matches exactly
        const translationParts = cleanTarget.split(/[,;]/).map(t => t.trim()).filter(t => t.length > 0);
        const isCorrect = translationParts.some(t => t === cleanInput) || cleanTarget === cleanInput;

        setSuccess(isCorrect);
        setSubmitted(true);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            check();
        }
    };

    if (submitted) {
        // Use fetched translation if available, otherwise fall back to word.translation
        const correctAnswer = translation || word.translation;
        return (
            <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                 <Feedback 
                     success={success} 
                     onNext={() => onComplete(success, input, correctAnswer || undefined)} 
                     correctAnswer={correctAnswer}
                     userAnswer={input || ''}
                 />
            </div>
        );
    }

    if (isFetchingTranslation) {
        return (
            <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
                <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-600 mb-4" />
                    <p className="text-gray-600">Fetching translation...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
             <h3 className="text-lg font-medium text-gray-700 mb-2">Translate this word</h3>
             <h1 className="text-3xl font-bold text-gray-800 mb-8">{word.word}</h1>
             <input
                type="text"
                className="w-full border-2 border-gray-200 rounded-lg p-3 focus:border-purple-500 focus:outline-none transition-colors mb-4 text-center"
                placeholder="Enter translation... (Press Enter to submit)"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={submitted || isFetchingTranslation}
            />
            <button 
                onClick={check} 
                disabled={submitted || !input.trim() || isFetchingTranslation}
                className="w-full py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isFetchingTranslation ? 'Fetching translation...' : 'Check'}
            </button>
        </div>
    );
};

interface ImageOption {
    url: string;
    word: string;
    isCorrect: boolean;
}

export const ImageLinking: React.FC<ActivityProps> = ({ word, onComplete, onUpdateWord }) => {
    const [imageOptions, setImageOptions] = useState<ImageOption[]>([]);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);

    // Automatically speak the word when component loads (after images are loaded)
    useEffect(() => {
        if (word?.word && !loading && imageOptions.length > 0) {
            const timer = setTimeout(() => {
                speakText(word.word, 'en-US');
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [word?.word, loading, imageOptions.length]);

    useEffect(() => {
        const loadImages = async () => {
            setLoading(true);
            try {
                // Get correct image for the word
                const correctImageResult = await fetchImageWithFallback(word.word);
                
                // Get random words for distractors and ensure they have images
                const distractors: ImageOption[] = [];
                let attempts = 0;
                
                while (distractors.length < 3 && attempts < 10) {
                    attempts++;
                    const randomWords = getRandomWords(3 - distractors.length);
                    
                    const distractorPromises = randomWords.map(async (randomWord) => {
                        const imageResult = await fetchImageWithFallback(randomWord);
                        return {
                            url: imageResult?.url || '',
                            word: randomWord,
                            isCorrect: false
                        };
                    });

                    const newDistractors = await Promise.all(distractorPromises);
                    
                    for (const d of newDistractors) {
                        if (d.url && !distractors.find(existing => existing.word === d.word) && d.word.toLowerCase() !== word.word.toLowerCase()) {
                            distractors.push(d);
                            if (distractors.length >= 3) break;
                        }
                    }
                }
                
                // Create options array with correct image and distractors
                const options: ImageOption[] = [
                    {
                        url: correctImageResult?.url || '',
                        word: word.word,
                        isCorrect: true
                    },
                    ...distractors
                ];

                // Shuffle the options
                const shuffled = options.sort(() => Math.random() - 0.5);
                setImageOptions(shuffled);
            } catch (error) {
                console.error('Error loading images:', error);
                setImageOptions([]);
            } finally {
                setLoading(false);
            }
        };

        loadImages();
    }, [word.word]);

    const handleSelect = (imageUrl: string) => {
        if (submitted) return;
        setSelectedImage(imageUrl);
    };

    const handleCheck = () => {
        if (!selectedImage) return;
        setSubmitted(true);
        const selectedOption = imageOptions.find(opt => opt.url === selectedImage);
        const isCorrect = selectedOption?.isCorrect || false;
        
        // Save the correct image to the word if it was selected
        if (isCorrect && selectedImage && onUpdateWord) {
            onUpdateWord({ imageUrl: selectedImage }).catch(console.error);
        }
    };

    if (loading) {
        return (
            <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
                <p className="text-gray-500">Loading images...</p>
            </div>
        );
    }

    if (submitted) {
        const selectedOption = imageOptions.find(opt => opt.url === selectedImage);
        const correctOption = imageOptions.find(opt => opt.isCorrect);
        const isCorrect = selectedOption?.isCorrect || false;
        return (
            <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <Feedback success={isCorrect} onNext={() => onComplete(isCorrect)} />
                {correctOption && (
                    <div className={`mt-4 p-4 rounded-lg text-center ${isCorrect ? 'bg-green-50' : 'bg-gray-50'}`}>
                        <p className="text-sm text-gray-500 mb-2">
                            {isCorrect ? 'Correct! ' : 'Correct image for: '}
                            <span className="font-medium text-green-700">{word.word}</span>
                        </p>
                        {correctOption.url && (
                            <img referrerPolicy="no-referrer" 
                                src={correctOption.url} 
                                alt={word.word} 
                                className={`w-full h-32 object-cover rounded-lg mx-auto border-2 ${isCorrect ? 'border-green-500' : 'border-green-500'}`}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    }

    if (imageOptions.length === 0) {
        return (
            <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
                <p className="text-gray-500 mb-4">Could not load images. This activity will be skipped.</p>
                <button onClick={() => onComplete(true)} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                    Continue
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
             <h3 className="text-lg font-medium text-gray-700 mb-4">Select the image for: <span className="font-bold">{word.word}</span></h3>

             <div className="grid grid-cols-2 gap-4 mb-6">
                 {imageOptions.map((option, index) => (
                     <button
                        key={index}
                        onClick={() => handleSelect(option.url)}
                        className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                            selectedImage === option.url 
                                ? 'border-purple-600 ring-2 ring-purple-300' 
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                        disabled={submitted}
                     >
                         {option.url ? (
                             <img referrerPolicy="no-referrer" 
                                 src={option.url} 
                                 alt={option.word} 
                                 className="w-full h-32 object-cover"
                             />
                         ) : (
                             <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
                                 <ImageIcon className="text-gray-400 w-8 h-8" />
                             </div>
                         )}
                         {selectedImage === option.url && (
                             <div className="absolute top-2 right-2 bg-purple-600 text-white rounded-full p-1">
                                 <Check className="w-4 h-4" />
                             </div>
                         )}
                     </button>
                 ))}
             </div>

             <button 
                onClick={handleCheck} 
                disabled={!selectedImage || submitted} 
                className="w-full py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
                Check Answer
            </button>
        </div>
    );
};

export const SpeakWord: React.FC<ActivityProps> = ({ word, onComplete }) => {
    const [selectedExample, setSelectedExample] = useState<{ text: string } | null>(null);
    const [loading, setLoading] = useState(false);
    const [score, setScore] = useState<number | null>(null);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    
    const { isRecording, transcript, startRecording, stopRecording } = useSpeechRecognition({
        isFromChatInput: false,
        isFromPractice: true
    });
    const { cancel: cancelSpeech } = useSpeechSynthesis({ lang: 'en-US' });

    const initialPracticeState = {
        isRecording: false,
        transcription: '',
        audioBlob: null,
        attempts: 0,
        score: null,
    };

    const [practiceState, setPracticeState] = useState(initialPracticeState);

    useEffect(() => {
        // For SpeakWord, use only the word, not a sentence
        if (word?.word) {
            setSelectedExample({ text: word.word });
            setLoading(false);
        }
    }, [word.word]);

    // Automatically speak the word when component loads
    useEffect(() => {
        if (selectedExample?.text && !loading && !hasSubmitted) {
            const timer = setTimeout(() => {
                speakText(selectedExample.text, 'en-US');
            }, 500); // Small delay to ensure voices are loaded
            return () => clearTimeout(timer);
        }
    }, [selectedExample?.text, loading, hasSubmitted]);

    useEffect(() => {
        setPracticeState(prevState => ({
            ...prevState,
            isRecording: isRecording,
            transcription: transcript,
        }));
    }, [isRecording, transcript]);

    const handleStartRecording = () => {
        // Prevent starting if already recording
        if (isRecording) {
            return;
        }
        // Stop any active speech synthesis before starting recording
        cancelSpeech();
        // Start recording immediately to preserve user gesture context
        if (!isRecording) {
            startRecording();
            setPracticeState(prevState => ({
                ...prevState,
                attempts: prevState.attempts + 1,
            }));
        }
    };

    const handleClosePractice = () => {
        setSelectedExample(null);
        setPracticeState(initialPracticeState);
        stopRecording();
    };

    // Calculate comparison results to check for mispronounced words
    const getComparisonResults = () => {
        if (!selectedExample?.text || !practiceState.transcription) return null;
        
        const cleanedExample = selectedExample.text
            .replace(/'re\b/g, ' are')
            .replace(/'ve\b/g, ' have')
            .replace(/'d\b/g, ' would')
            .replace(/'ll\b/g, ' will')
            .replace(/{\/?it}|\/it\//g, '')
            .replace(/'m\b/g, ' am')
            .replace(/―/g, '')
            .replace(/,/g, '')
            .replace(/\//g, ' ')
            .replace(/n't\b/g, ' not')
            .replace(/{\/?phrase}/g, '')
            .replace(/\bok\b/g, 'okay')
            .replace(/\bOK\b/g, 'okay')
            .replace(/\bcan't\b/g, 'cannot')
            .replace('=', '')
            .replace('[', '')
            .replace(']', '')
            .replace(';', '')
            .replace(/\.\.\./g, '')
            .replace(/…/g, '')
            .replace(/\bwon't\b/g, 'will not')
            .trim();
        
        return compareTextsV2(cleanedExample, practiceState.transcription);
    };

    const handleSubmit = () => {
        if (hasSubmitted) return;
        
        setHasSubmitted(true);
        // Score is set by PracticeModal via setScoreActivity
        const success = score !== null && score >= 50;
        // Don't call onComplete immediately - show feedback first
    };

    const handleSkip = () => {
        if (hasSubmitted) return;
        
        setHasSubmitted(true);
        // Stop recording if active
        if (isRecording) {
            stopRecording();
        }
        // Skip without penalty - mark as completed but not successful
    };

    const handleNext = () => {
        const success = score !== null && score >= 50;
        onComplete(success, practiceState.transcription || '', selectedExample?.text || '', score !== null ? score : undefined);
    };

    // Show feedback when submitted
    if (hasSubmitted) {
        const comparisonResults = getComparisonResults();
        const success = score !== null && score >= 50;
        
        return (
            <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <Feedback 
                    success={success} 
                    onNext={handleNext}
                    correctAnswer={selectedExample?.text || null}
                    userAnswer={practiceState.transcription || ''}
                    comparisonResults={comparisonResults}
                />
            </div>
        );
    }

    if (loading || !selectedExample) {
        return (
            <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
                <p className="text-gray-500">Loading...</p>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between p-3 sm:p-6 pb-0">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Practice your speaking</h3>
                {!hasSubmitted && (
                    <button
                        onClick={handleSkip}
                        disabled={hasSubmitted}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        title="Skip this activity if you can't speak"
                    >
                        <SkipForward className="w-4 h-4" />
                        <span>Skip</span>
                    </button>
                )}
            </div>
            
            <PracticeModal
                example={selectedExample}
                practiceState={practiceState}
                onClose={handleClosePractice}
                onStartRecording={handleStartRecording}
                onStopRecording={stopRecording}
                isRecording={isRecording}
                activity={true}
                setScoreActivity={setScore}
                disableTryAgain={false}
                isWordOnly={true}
            />
            
            {!hasSubmitted && score !== null && !isRecording && (
                <div className="p-6 pt-0">
                    <button 
                        onClick={handleSubmit} 
                        disabled={hasSubmitted}
                        className="w-full py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50"
                    >
                        Submit
                    </button>
                </div>
            )}
        </div>
    );
};

export const SpeakPhrase: React.FC<ActivityProps> = ({ word, onComplete }) => {
    const [selectedExample, setSelectedExample] = useState<{ text: string } | null>(null);
    const [loading, setLoading] = useState(true);
    const [score, setScore] = useState<number | null>(null);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    
    const { isRecording, transcript, startRecording, stopRecording } = useSpeechRecognition({
        isFromChatInput: false,
        isFromPractice: true
    });
    const { cancel: cancelSpeech } = useSpeechSynthesis({ lang: 'en-US' });

    const initialPracticeState = {
        isRecording: false,
        transcription: '',
        audioBlob: null,
        attempts: 0,
        score: null,
    };

    const [practiceState, setPracticeState] = useState(initialPracticeState);

    useEffect(() => {
        const fetchExample = async () => {
            setLoading(true);
            try {
                // For SpeakPhrase, we need a sentence example, not just the word
                let exampleText = word.exampleSentence;
                
                if (!exampleText) {
                    if (word.word.trim().split(/\s+/).length > 1) {
                        // If it's already a phrase or sentence, just use it directly
                        exampleText = word.word;
                    } else {
                        const dictResult = await searchWord(word.word);
                        if (dictResult.examples && dictResult.examples.length > 0) {
                            // Find an example that contains the word
                            exampleText = dictResult.examples.find(ex => 
                                ex.toLowerCase().includes(word.word.toLowerCase())
                            ) || dictResult.examples[0];
                        } else if (dictResult.meanings.length > 0) {
                            // Try to get example from definitions
                            const exampleFromDef = dictResult.meanings
                                .flatMap(m => m.definitions)
                                .find(d => d.example && d.example.toLowerCase().includes(word.word.toLowerCase()));
                            exampleText = exampleFromDef?.example || `A sentence with "${word.word}"`;
                        } else {
                            exampleText = `A sentence with "${word.word}"`;
                        }
                    }
                }
                
                setSelectedExample({ text: exampleText });
            } catch (error) {
                console.error('Error fetching example:', error);
                setSelectedExample({ text: `A sentence with "${word.word}"` });
            } finally {
                setLoading(false);
            }
        };

        fetchExample();
    }, [word.word, word.exampleSentence]);

    // Automatically speak the phrase when component loads
    useEffect(() => {
        if (selectedExample?.text && !loading) {
            const timer = setTimeout(() => {
                speakText(selectedExample.text, 'en-US');
            }, 500); // Small delay to ensure voices are loaded
            return () => clearTimeout(timer);
        }
    }, [selectedExample?.text, loading]);

    useEffect(() => {
        setPracticeState(prevState => ({
            ...prevState,
            isRecording: isRecording,
            transcription: transcript,
        }));
    }, [isRecording, transcript]);

    const handleStartRecording = () => {
        // Prevent starting if already recording
        if (isRecording) {
            return;
        }
        // Stop any active speech synthesis before starting recording
        cancelSpeech();
        // Start recording immediately to preserve user gesture context
        if (!isRecording) {
            startRecording();
            setPracticeState(prevState => ({
                ...prevState,
                attempts: prevState.attempts + 1,
            }));
        }
    };

    const handleClosePractice = () => {
        setSelectedExample(null);
        setPracticeState(initialPracticeState);
        stopRecording();
    };

    // Calculate comparison results to check for mispronounced words
    const getComparisonResults = () => {
        if (!selectedExample?.text || !practiceState.transcription) return null;
        
        // Clean the example text the same way PracticeModal does
        const cleanedExample = selectedExample.text
            .replace(/'re\b/g, ' are')
            .replace(/'ve\b/g, ' have')
            .replace(/'d\b/g, ' would')
            .replace(/'ll\b/g, ' will')
            .replace(/{\/?it}|\/it\//g, '')
            .replace(/'m\b/g, ' am')
            .replace(/―/g, '')
            .replace(/,/g, '')
            .replace(/\//g, ' ')
            .replace(/n't\b/g, ' not')
            .replace(/{\/?phrase}/g, '')
            .replace(/\bok\b/g, 'okay')
            .replace(/\bOK\b/g, 'okay')
            .replace(/\bcan't\b/g, 'cannot')
            .replace('=', '')
            .replace('[', '')
            .replace(']', '')
            .replace(';', '')
            .replace(/\.\.\./g, '') // Remove ellipsis
            .replace(/…/g, '') // Remove ellipsis character
            .replace(/\bwon't\b/g, 'will not')
            .trim();
        
        return compareTextsV2(cleanedExample, practiceState.transcription);
    };

    const handleSubmit = () => {
        if (hasSubmitted) return;
        
        setHasSubmitted(true);
        // Score is set by PracticeModal via setScoreActivity
        const success = score !== null && score >= 50;
        // Don't call onComplete immediately - show feedback first
    };

    const handleSkip = () => {
        if (hasSubmitted) return;
        
        setHasSubmitted(true);
        // Stop recording if active
        if (isRecording) {
            stopRecording();
        }
        // Skip without penalty - mark as completed but not successful
    };

    const handleNext = () => {
        const success = score !== null && score >= 50;
        onComplete(success, practiceState.transcription || '', selectedExample?.text || '', score !== null ? score : undefined);
    };

    // Show feedback when submitted
    if (hasSubmitted) {
        const comparisonResults = getComparisonResults();
        const success = score !== null && score >= 50;
        
        return (
            <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <Feedback 
                    success={success} 
                    onNext={handleNext}
                    correctAnswer={selectedExample?.text || null}
                    userAnswer={practiceState.transcription || ''}
                    comparisonResults={comparisonResults}
                />
            </div>
        );
    }

    if (loading || !selectedExample) {
        return (
            <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
                <p className="text-gray-500">Loading...</p>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between p-3 sm:p-6 pb-0">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Practice your speaking</h3>
                {!hasSubmitted && (
                    <button
                        onClick={handleSkip}
                        disabled={hasSubmitted}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        title="Skip this activity if you can't speak"
                    >
                        <SkipForward className="w-4 h-4" />
                        <span>Skip</span>
                    </button>
                )}
            </div>
            
            <PracticeModal
                example={selectedExample}
                practiceState={practiceState}
                onClose={handleClosePractice}
                onStartRecording={handleStartRecording}
                onStopRecording={stopRecording}
                isRecording={isRecording}
                activity={true}
                setScoreActivity={setScore}
                disableTryAgain={false}
            />
            
            {!hasSubmitted && score !== null && !isRecording && (
                <div className="p-6 pt-0">
                    <button 
                        onClick={handleSubmit} 
                        disabled={hasSubmitted}
                        className="w-full py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50"
                    >
                        Submit
                    </button>
                </div>
            )}
        </div>
    );
};

export const Synonyms: React.FC<ActivityProps> = ({ word, onComplete, onUpdateWord }) => {
    const [selected, setSelected] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [options, setOptions] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [correctAnswer, setCorrectAnswer] = useState<string | null>(null);

    // Automatically speak the word when component loads (after synonyms are fetched)
    useEffect(() => {
        if (word?.word && !loading && options.length > 0) {
            // Small delay to ensure voices are loaded
            const timer = setTimeout(() => {
                speakText(word.word, 'en-US');
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [word?.word, loading, options.length]);

    useEffect(() => {
        const fetchSynonyms = async () => {
            setLoading(true);
            try {
                // First check if word already has synonyms
                const existingSynonyms = word.synonyms || [];
                
                let synonyms: string[] = [];
                if (existingSynonyms.length > 0) {
                    synonyms = existingSynonyms;
                } else {
                    // Fetch from dictionary API
                    const dictResult = await searchWord(word.word);
                    synonyms = dictResult.synonyms || [];
                    
                    // If no synonyms from API, try getting from meanings
                    if (synonyms.length === 0 && dictResult.meanings.length > 0) {
                        synonyms = dictResult.meanings
                            .flatMap(m => m.definitions.flatMap(d => d.synonyms || []))
                            .filter((s, i, arr) => arr.indexOf(s) === i) // Remove duplicates
                            .slice(0, 5);
                    }
                    
                    // If still no synonyms, try Datamuse API directly as fallback
                    if (synonyms.length === 0) {
                        try {
                            const datamuseResponse = await fetch(`https://api.datamuse.com/words?rel_syn=${encodeURIComponent(word.word)}&max=10`);
                            if (datamuseResponse.ok) {
                                const datamuseData = await datamuseResponse.json();
                                if (Array.isArray(datamuseData) && datamuseData.length > 0) {
                                    synonyms = datamuseData
                                        .map((item: any) => item.word)
                                        .filter((w: string) => w.toLowerCase() !== word.word.toLowerCase())
                                        .slice(0, 5);
                                }
                            }
                        } catch (error) {
                            console.error('Error fetching synonyms from Datamuse:', error);
                        }
                    }
                }

                if (synonyms.length > 0) {
                    const correct = synonyms[0];
                    setCorrectAnswer(correct);
                    
                    // Get random words as wrong options
                    const randomWords = getRandomWords(3);
                    const distractors = randomWords.filter(d => !synonyms.includes(d) && d.toLowerCase() !== word.word.toLowerCase());
                    
                    // Ensure we have 3 distractors, pad with more random words if needed
                    while (distractors.length < 3) {
                        const moreRandom = getRandomWords(1);
                        if (moreRandom[0] && !synonyms.includes(moreRandom[0]) && !distractors.includes(moreRandom[0]) && moreRandom[0].toLowerCase() !== word.word.toLowerCase()) {
                            distractors.push(moreRandom[0]);
                        } else {
                            break; // Avoid infinite loop
                        }
                    }
                    
                    const opts = [correct, ...distractors.slice(0, 3)].sort(() => Math.random() - 0.5);
                    setOptions(opts);
                } else {
                    setOptions([]);
                }
            } catch (error) {
                console.error('Error fetching synonyms:', error);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchSynonyms();
    }, [word.id, word.word]);

    const check = () => {
        if (!selected) return;
        setSubmitted(true);
    };

    if (loading) {
        return (
            <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
                <p className="text-gray-500">Loading synonyms...</p>
            </div>
        );
    }

    if (submitted) {
        const isCorrect = selected === correctAnswer;
        return <Feedback success={isCorrect} onNext={() => onComplete(isCorrect, selected, correctAnswer || undefined)} correctAnswer={correctAnswer} standalone={true} />;
    }

    if (options.length === 0) {
         // Fallback UI when no synonyms exist - just skip
         return (
            <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
                 <h3 className="text-lg font-medium text-gray-700 mb-4">No synonyms found for <span className="font-bold">{word.word}</span></h3>
                 <p className="text-gray-500 mb-6">This activity will be skipped.</p>
                 <button onClick={() => onComplete(true)} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                     Continue
                 </button>
            </div>
         );
    }

    return (
        <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
             <h3 className="text-lg font-medium text-gray-700 mb-6">Select a synonym or a similar word for: <span className="font-bold text-purple-600">{word.word}</span></h3>

             <div className="grid grid-cols-1 gap-3 mb-6">
                 {options.map(opt => (
                     <button
                        key={opt}
                        onClick={() => {
                            setSelected(opt);
                            // Speak the selected synonym
                            speakText(opt, 'en-US');
                        }}
                        className={`p-4 rounded-lg border-2 transition-all font-medium ${selected === opt ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-100 hover:border-gray-200'}`}
                     >
                         {opt}
                     </button>
                 ))}
             </div>

             <button onClick={check} disabled={!selected} className="w-full py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50">
                Check Answer
            </button>
        </div>
    );
};

export const ListenAndArrange: React.FC<ActivityProps> = ({ word, onComplete }) => {
    const [selectedWords, setSelectedWords] = useState<string[]>([]);
    const [availableWords, setAvailableWords] = useState<string[]>([]);
    const [submitted, setSubmitted] = useState(false);
    const [success, setSuccess] = useState(false);
    const [currentInput, setCurrentInput] = useState('');
    const [targetSentence, setTargetSentence] = useState('');
    const [isWordOnly, setIsWordOnly] = useState(false);
    const [suggestion, setSuggestion] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [accent, setAccent] = useState<'en-US' | 'en-GB'>('en-US');
    const inputRef = useRef<HTMLInputElement>(null);
    const { cancel, speak, isSpeaking } = useSpeechSynthesis({ lang: 'en-US' });

    // Speak the sentence at normal speed
    const handlePlayNormal = useCallback((overrideLang?: 'en-US' | 'en-GB') => {
        if (!targetSentence) return;
        const targetLang = overrideLang || accent;
        if (overrideLang) {
            setAccent(overrideLang);
        }
        speak(targetSentence, targetLang, 0.85);
    }, [targetSentence, speak, accent]);

    // Get the sentence/word to use (prefer dictionary examples, then exampleSentence, fallback to word)
    useEffect(() => {
        const initializeSentence = async () => {
            setIsLoading(true);
            let sentence = word.word;
            let hasSentence = false;
            
            // First, check if exampleSentence exists and is a proper sentence
            if (word.exampleSentence && word.exampleSentence.trim().split(/\s+/).length > 1) {
                sentence = word.exampleSentence;
                hasSentence = true;
            } else {
                // Try to fetch dictionary examples
                try {
                    const dictResult = await searchWord(word.word);
                    if (dictResult && dictResult.examples && dictResult.examples.length > 0) {
                        // Filter examples that are actual sentences (more than 1 word)
                        const sentenceExamples = dictResult.examples.filter(ex => 
                            ex && ex.trim().split(/\s+/).length > 1
                        );
                        
                        if (sentenceExamples.length > 0) {
                            // Pick a random example sentence
                            const randomExample = sentenceExamples[Math.floor(Math.random() * sentenceExamples.length)];
                            // Clean up the example (remove any formatting, extra spaces, etc.)
                            sentence = randomExample.trim().replace(/\s+/g, ' ');
                            hasSentence = true;
                        }
                    }
                } catch (error) {
                    console.error('Error fetching dictionary examples:', error);
                    // Fallback to word if dictionary fetch fails
                }
            }
            
            // Remove any formatting tags like {it}, {/it}, {phrase} before using
            sentence = sentence.replace(/{\/?it}|\/it\//g, '').replace(/{\/?phrase}/g, '').replace(/\s+/g, ' ').trim();

            const isSingleWord = !hasSentence;
            setIsWordOnly(isSingleWord);
            setTargetSentence(sentence);
            
            // Split sentence into words and create word bank with distractors
            // Remove punctuation for word bank
            const cleanSentence = sentence.replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, '');
            const words = cleanSentence.toLowerCase().split(/\s+/).filter(w => w.length > 0);
            
            // Get random words as distractors
            const distractors = getRandomWords(4).filter(d => 
                !words.some(w => w.toLowerCase() === d.toLowerCase())
            );
            
            // Combine actual words with distractors and shuffle
            const allWords = [...words, ...distractors.slice(0, Math.max(0, 6 - words.length))]
                .sort(() => Math.random() - 0.5);
            
            setAvailableWords(allWords);
            setIsLoading(false);
        };
        
        initializeSentence();
    }, [word]);

    // Automatically play the sentence when component mounts or targetSentence changes
    useEffect(() => {
        if (targetSentence && !submitted) {
            // Small delay to ensure voices are loaded
            const timer = setTimeout(() => {
                handlePlayNormal();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [targetSentence, submitted, handlePlayNormal]);

    // Speak the sentence at slow speed
    const handlePlaySlow = () => {
        if (!targetSentence) return;
        speak(targetSentence, accent, 0.6);
    };

    // Find auto-complete suggestion based on current input
    const findSuggestion = useCallback((input: string): string => {
        if (!input.trim()) return '';
        
        const inputLower = input.toLowerCase().trim();
        // Get unique available words that start with the input
        const uniqueAvailableWords = Array.from(new Set(availableWords));
        const matches = uniqueAvailableWords.filter(w => 
            w.toLowerCase().startsWith(inputLower)
        );
        
        // If exactly one match, return it
        if (matches.length === 1) {
            return matches[0];
        }
        
        return '';
    }, [availableWords]);

    // Handle keyboard input with auto-complete
    const handleInputChange = (value: string) => {
        if (submitted) return;
        
        // Mobile keyboards often don't trigger keydown events reliably for spaces.
        // If the value ends with a space and we have a suggestion, accept it.
        if (value.endsWith(' ') && suggestion && currentInput.trim()) {
            acceptSuggestion(suggestion);
            return;
        }
        
        setCurrentInput(value);
        
        // Check for auto-complete suggestion
        const newSuggestion = findSuggestion(value);
        setSuggestion(newSuggestion);
    };

    // Handle space or tab to accept auto-complete
    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (submitted) return;
        
        if (e.key === 'Enter') {
            e.preventDefault();
            handleCheck();
            return;
        }
        
        // Auto-complete on Space if there's a unique suggestion
        if (e.key === ' ' && suggestion && currentInput.trim()) {
            e.preventDefault();
            acceptSuggestion(suggestion);
            return;
        }
        
        // Accept suggestion on Tab
        if (e.key === 'Tab' && suggestion && currentInput.trim()) {
            e.preventDefault();
            acceptSuggestion(suggestion);
            return;
        }
        
        // Handle backspace to remove last word if input is empty
        if (e.key === 'Backspace' && !currentInput.trim() && selectedWords.length > 0) {
            e.preventDefault();
            removeLastWord();
        }
    };

    // Accept auto-complete suggestion
    const acceptSuggestion = (word: string) => {
        // Add word to selected
        setSelectedWords(prev => [...prev, word]);
        // Remove only ONE instance from available
        setAvailableWords(prev => {
            const index = prev.findIndex(w => w === word);
            if (index !== -1) {
                const newAvailable = [...prev];
                newAvailable.splice(index, 1);
                return newAvailable;
            }
            return prev;
        });
        // Clear input
        setCurrentInput('');
        setSuggestion('');
        // Focus back on input
        inputRef.current?.focus();
    };

    // Handle word selection from word bank
    const handleWordClick = (word: string) => {
        if (submitted) return;
        
        // Remove only ONE instance from available words and add to selected
        setAvailableWords(prev => {
            const index = prev.findIndex(w => w === word);
            if (index !== -1) {
                const newAvailable = [...prev];
                newAvailable.splice(index, 1);
                return newAvailable;
            }
            return prev;
        });
        setSelectedWords(prev => [...prev, word]);
        // Clear current input if it matches
        if (currentInput.toLowerCase().trim() === word.toLowerCase()) {
            setCurrentInput('');
            setSuggestion('');
        }
    };

    // Handle removing a word from selected sentence
    const handleRemoveWord = (index: number) => {
        if (submitted) return;
        
        const word = selectedWords[index];
        setSelectedWords(prev => prev.filter((_, i) => i !== index));
        setAvailableWords(prev => [...prev, word].sort(() => Math.random() - 0.5));
    };

    // Remove last word
    const removeLastWord = () => {
        if (selectedWords.length === 0) return;
        const lastWord = selectedWords[selectedWords.length - 1];
        setSelectedWords(prev => prev.slice(0, -1));
        setAvailableWords(prev => [...prev, lastWord].sort(() => Math.random() - 0.5));
    };

    // Handle check button
    const handleCheck = () => {
        // Combine selected words and current input
        const allWords = [...selectedWords];
        if (currentInput.trim()) {
            // If there's a suggestion and it matches, use it
            if (suggestion && suggestion.toLowerCase().startsWith(currentInput.toLowerCase().trim())) {
                allWords.push(suggestion);
            } else {
                // Otherwise, try to match the input with available words
                const match = availableWords.find(w => w.toLowerCase() === currentInput.toLowerCase().trim());
                if (match) {
                    allWords.push(match);
                } else {
                    allWords.push(currentInput.trim());
                }
            }
        }
        
        const userAnswer = allWords.join(' ');
        if (!userAnswer.trim()) return;
        
        const userWords = userAnswer.toLowerCase().trim().split(/\s+/).filter(w => w.length > 0);
        const targetWords = targetSentence.toLowerCase().split(/\s+/).filter(w => w.length > 0);
        
        // Remove punctuation for comparison
        const cleanUser = userWords.join(' ').replace(/[.,\/#!$%^&*;:{}=\-_`~()?"'[\]<>…]/g, '').trim();
        const cleanTarget = targetWords.join(' ').replace(/[.,\/#!$%^&*;:{}=\-_`~()?"'[\]<>…]/g, '').trim();
        
        const isCorrect = cleanUser === cleanTarget;
        setSuccess(isCorrect);
        setSubmitted(true);
    };

    // Handle "Can't hear now" - skip the activity
    const handleCantHear = () => {
        if (submitted) return;
        // Skip the activity - mark as completed but not successful
        onComplete(false, '', targetSentence);
    };

    if (isLoading) {
        return (
            <div className="max-w-2xl mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-600 mb-4" />
                    <p className="text-gray-600">Loading sentence from dictionary...</p>
                </div>
            </div>
        );
    }

    if (submitted) {
        const allWords = [...selectedWords];
        if (currentInput.trim() && suggestion) {
            allWords.push(suggestion);
        } else if (currentInput.trim()) {
            allWords.push(currentInput.trim());
        }
        const userSentence = allWords.join(' ');
        return (
            <div className="max-w-2xl mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <Feedback 
                    success={success} 
                    onNext={() => onComplete(success, userSentence, targetSentence)} 
                    correctAnswer={targetSentence}
                />
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            {/* Instruction */}
            <h3 className="text-xl font-medium text-gray-800 mb-6 text-center">
                Tap what you hear:
            </h3>

            {/* Audio Controls */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
                <button
                    onClick={() => handlePlayNormal('en-US')}
                    disabled={isSpeaking}
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md ${
                        accent === 'en-US'
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                    }`}
                >
                    <Volume2 className="w-5 h-5" />
                    <span>🇺🇸 Play US</span>
                </button>
                <button
                    onClick={() => handlePlayNormal('en-GB')}
                    disabled={isSpeaking}
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md ${
                        accent === 'en-GB'
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                            : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'
                    }`}
                >
                    <Volume2 className="w-5 h-5" />
                    <span>🇬🇧 Play UK</span>
                </button>
                <button
                    onClick={handlePlaySlow}
                    disabled={isSpeaking}
                    className="flex items-center gap-2 px-5 py-3 bg-blue-400 text-white rounded-xl font-semibold hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                >
                    <Gauge className="w-5 h-5" />
                    <span>Slow ({accent === 'en-GB' ? 'UK' : 'US'})</span>
                </button>
            </div>

            {/* Combined Input Area - Shows selected words and typing input */}
            <div className="mb-6">
                <div className="min-h-[80px] p-4 bg-gray-50 rounded-lg border-2 border-gray-300 flex flex-wrap gap-2 items-center focus-within:border-purple-500 transition-colors">
                    {/* Selected Words as Chips */}
                    {selectedWords.map((word, index) => (
                        <button
                            key={`${word}-${index}`}
                            onClick={() => handleRemoveWord(index)}
                            className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-medium hover:bg-blue-200 transition-colors flex items-center gap-2"
                        >
                            {word}
                            <X className="w-4 h-4" />
                        </button>
                    ))}
                    
                    {/* Input Field with Auto-complete */}
                    <div className="flex-1 min-w-[200px] relative">
                        <div className="relative">
                            <input
                                ref={inputRef}
                                type="text"
                                value={currentInput}
                                onChange={(e) => handleInputChange(e.target.value)}
                                onKeyDown={handleInputKeyDown}
                                placeholder={selectedWords.length === 0 ? `Type or tap words to form the ${isWordOnly ? 'word' : 'sentence'}...` : ''}
                                className="w-full bg-transparent border-none outline-none text-lg text-gray-800 placeholder-gray-400 relative z-10"
                            />
                            {/* Inline suggestion overlay */}
                            {suggestion && currentInput.trim() && suggestion.toLowerCase().startsWith(currentInput.toLowerCase().trim()) && (
                                <div className="absolute left-0 top-0 text-lg text-gray-400 pointer-events-none z-0">
                                    {currentInput}
                                    <span className="text-gray-300">
                                        {suggestion.slice(currentInput.length)}
                                    </span>
                                </div>
                            )}
                        </div>
                        {/* Auto-complete hint */}
                        {suggestion && currentInput.trim() && (
                            <div className="absolute left-0 top-full mt-1 text-xs text-gray-500">
                                Press <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs font-mono">Space</kbd> to complete: <span className="font-semibold text-purple-600">{suggestion}</span>
                            </div>
                        )}
                    </div>
                </div>
                {selectedWords.length === 0 && !currentInput && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                        Type initial letters and words will auto-complete, or tap words from the bank below
                    </p>
                )}
            </div>

            {/* Word Bank */}
            <div className="mb-6">
                <p className="text-sm text-gray-600 mb-3 font-medium">Word Bank:</p>
                <div className="flex flex-wrap gap-2">
                    {availableWords.map((word, index) => (
                        <button
                            key={`${word}-${index}`}
                            onClick={() => handleWordClick(word)}
                            disabled={submitted}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {word}
                        </button>
                    ))}
                </div>
            </div>

            {/* Bottom Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-200">
                <button
                    onClick={handleCantHear}
                    className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
                >
                    Can't hear now
                </button>

                <button
                    onClick={handleCheck}
                    disabled={!currentInput.trim() && selectedWords.length === 0}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Check
                </button>
            </div>
        </div>
    );
};

export const FillInTheBlanks: React.FC<ActivityProps> = ({ word, onComplete }) => {
    const [options, setOptions] = useState<string[]>([]);
    const [selected, setSelected] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        // Fetch decoys
        const decoys = getDecoyWordsForTheme(3, word.word, word.theme);
        // Mix with correct word
        const allOptions = [word.word, ...decoys].sort(() => Math.random() - 0.5);
        setOptions(allOptions);
    }, [word.word]);

    const check = (selectedWord: string) => {
        if (submitted) return;
        setSelected(selectedWord);
        const isCorrect = selectedWord.toLowerCase() === word.word.toLowerCase();
        setSuccess(isCorrect);
        setSubmitted(true);
    };

    if (submitted) {
        return (
            <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <Feedback 
                    success={success} 
                    onNext={() => onComplete(success, selected || undefined, word.word)} 
                    correctAnswer={word.word}
                    userAnswer={selected || ''}
                />
            </div>
        );
    }

    const sentence = word.exampleSentence || `This is a sentence about ${word.word}.`;
    const regex = new RegExp(`\\b${word.word}\\b`, 'gi');
    const displaySentence = sentence.replace(regex, '________');

    return (
        <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
            <h3 className="text-lg font-medium text-gray-700 mb-6">Fill in the blank</h3>
            <p className="text-xl font-medium text-gray-800 mb-8">{displaySentence}</p>
            <div className="grid grid-cols-2 gap-4">
                {options.map((opt, i) => (
                    <button 
                        key={i}
                        onClick={() => check(opt)}
                        className="py-3 px-4 border-2 border-purple-200 rounded-lg text-purple-700 font-semibold hover:bg-purple-50 hover:border-purple-300 transition-colors"
                    >
                        {opt}
                    </button>
                ))}
            </div>
        </div>
    );
};

export const Matching: React.FC<ActivityProps> = ({ word, onComplete }) => {
    const [loading, setLoading] = useState(true);
    const [enList, setEnList] = useState<{id: string, text: string, matched: boolean}[]>([]);
    const [ptList, setPtList] = useState<{id: string, text: string, matched: boolean}[]>([]);
    
    const [selectedEn, setSelectedEn] = useState<string | null>(null);
    const [selectedPt, setSelectedPt] = useState<string | null>(null);

    const [incorrectAttempts, setIncorrectAttempts] = useState(0);

    useEffect(() => {
        let isMounted = true;

        const loadPairs = async () => {
            try {
                // 1. Get the target word and its translation
                let targetPt = word.translation;
                if (!targetPt) {
                    targetPt = await translateText(word.word, 'en', 'pt');
                }
                const targetPair = { en: word.word, pt: targetPt || word.word };
                
                // 2. Get 7 decoy words
                const decoyWords = getDecoyWordsForTheme(7, word.word, word.theme);
                
                // 3. Translate decoys
                const decoyPairs = await Promise.all(decoyWords.map(async (dw) => {
                    let pt = await translateText(dw, 'en', 'pt');
                    if (!pt || pt.toLowerCase() === dw.toLowerCase()) {
                        pt = `[${dw}]`; 
                    }
                    return { en: dw, pt };
                }));
                
                if (!isMounted) return;

                const allPairs = [targetPair, ...decoyPairs];

                const enItems = allPairs.map(p => ({ id: p.en, text: p.en, matched: false })).sort(() => Math.random() - 0.5);
                const ptItems = allPairs.map(p => ({ id: p.en, text: p.pt, matched: false })).sort(() => Math.random() - 0.5);

                setEnList(enItems);
                setPtList(ptItems);
                setLoading(false);

            } catch (error) {
                console.error("Failed to load matching pairs", error);
                if (isMounted) setLoading(false);
            }
        };

        loadPairs();

        return () => { isMounted = false; };
    }, [word.word, word.translation]);

    useEffect(() => {
        if (selectedEn && selectedPt) {
            if (selectedEn === selectedPt) {
                playCorrectSound();
                setEnList(prev => prev.map(item => item.id === selectedEn ? { ...item, matched: true } : item));
                setPtList(prev => prev.map(item => item.id === selectedPt ? { ...item, matched: true } : item));
                setSelectedEn(null);
                setSelectedPt(null);
            } else {
                playWrongSound();
                setIncorrectAttempts(prev => prev + 1);
                setTimeout(() => {
                    setSelectedEn(null);
                    setSelectedPt(null);
                }, 500);
            }
        }
    }, [selectedEn, selectedPt]);

    useEffect(() => {
        if (!loading && enList.length > 0 && enList.every(item => item.matched)) {
            const score = Math.max(50, 100 - (incorrectAttempts * 10));
            setTimeout(() => {
                onComplete(true, undefined, undefined, score);
            }, 1000);
        }
    }, [enList, loading, incorrectAttempts, onComplete]);

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center min-h-[400px] flex flex-col items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600 mb-4" />
                <p className="text-gray-600">Preparing matching game...</p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-lg font-medium text-gray-700 mb-6 text-center">Match the English words with their Portuguese translations</h3>
            
            <div className="grid grid-cols-2 gap-8">
                <div className="space-y-3">
                    <h4 className="font-bold text-gray-500 mb-4 text-center">English</h4>
                    {enList.map(item => (
                        <button
                            key={`en-${item.id}`}
                            onClick={() => !item.matched && setSelectedEn(item.id)}
                            disabled={item.matched || (selectedEn !== null && selectedPt !== null)}
                            className={`w-full py-3 px-4 rounded-lg font-medium transition-all transform active:scale-95 ${
                                item.matched 
                                    ? 'bg-green-100 text-green-700 border-2 border-green-200 opacity-50 cursor-not-allowed'
                                    : selectedEn === item.id
                                        ? 'bg-purple-600 text-white shadow-md'
                                        : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-purple-300 hover:bg-purple-50'
                            }`}
                        >
                            {item.text}
                        </button>
                    ))}
                </div>

                <div className="space-y-3">
                    <h4 className="font-bold text-gray-500 mb-4 text-center">Portuguese</h4>
                    {ptList.map(item => (
                        <button
                            key={`pt-${item.id}`}
                            onClick={() => !item.matched && setSelectedPt(item.id)}
                            disabled={item.matched || (selectedEn !== null && selectedPt !== null)}
                            className={`w-full py-3 px-4 rounded-lg font-medium transition-all transform active:scale-95 ${
                                item.matched 
                                    ? 'bg-green-100 text-green-700 border-2 border-green-200 opacity-50 cursor-not-allowed'
                                    : selectedPt === item.id
                                        ? 'bg-purple-600 text-white shadow-md'
                                        : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-purple-300 hover:bg-purple-50'
                            }`}
                        >
                            {item.text}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};
